import { useMemo } from 'react';
import type { ChatWidgetProps } from '../types';

type ResolvePropFn = <T>(prop: T | undefined, paths: string[], fallback: T) => T;

interface UseEndpointConfigOptions {
  props: ChatWidgetProps;
  resolveProp: ResolvePropFn;
  apiKey: string | null;
  apiBaseUrl: string | null;
}

export function useEndpointConfig({
  props,
  resolveProp,
  apiKey,
  apiBaseUrl,
}: UseEndpointConfigOptions) {
  // Endpoints
  const widgetAuthToken = resolveProp(
    props.widgetAuthToken,
    ['security.widgetAuthToken', 'widgetAuthToken'],
    ''
  );
  const messageEndpoint = resolveProp(
    props.messageEndpoint,
    ['endpoints.messageEndpoint', 'messageEndpoint'],
    null
  );
  const messageCreateEndpoint = resolveProp(
    props.messageCreateEndpoint,
    ['endpoints.messageCreateEndpoint', 'messageCreateEndpoint'],
    null
  );
  const streamEndpoint = resolveProp(
    props.streamEndpoint,
    ['endpoints.streamEndpoint', 'streamEndpoint'],
    null
  );
  const threadEndpoint = resolveProp(
    props.threadEndpoint,
    ['endpoints.threadEndpoint', 'threadEndpoint'],
    null
  );
  const threadCreateEndpoint = resolveProp(
    props.threadCreateEndpoint,
    ['endpoints.threadCreateEndpoint', 'threadCreateEndpoint'],
    null
  );
  const threadUpdateEndpoint = resolveProp(
    props.threadUpdateEndpoint,
    ['endpoints.threadUpdateEndpoint', 'threadUpdateEndpoint'],
    null
  );
  const threadDeleteEndpoint = resolveProp(
    props.threadDeleteEndpoint,
    ['endpoints.threadDeleteEndpoint', 'threadDeleteEndpoint'],
    null
  );
  const cartEndpoint = resolveProp(
    props.cartEndpoint,
    ['endpoints.cartEndpoint', 'cartEndpoint'],
    null
  );

  // Analytics
  const analyticsBaseUrl = resolveProp(
    props.analyticsBaseUrl,
    ['endpoints.analyticsBaseUrl', 'analyticsBaseUrl'],
    null
  );
  const analyticsEndpoint = resolveProp(
    props.analyticsEndpoint,
    ['endpoints.analyticsEndpoint', 'analyticsEndpoint'],
    null
  );
  const analyticsSampleRate = resolveProp(
    props.analyticsSampleRate,
    ['analytics.sampleRate', 'analyticsSampleRate'],
    undefined
  );
  const analyticsEnabled = resolveProp(
    props.analyticsEnabled,
    ['analytics.enabled', 'analyticsEnabled'],
    analyticsBaseUrl ? true : false
  );
  const analyticsApiKey = resolveProp(
    props.analyticsApiKey,
    ['analytics.apiKey', 'analyticsApiKey'],
    apiKey
  );

  // Telemetry
  const telemetryEnabled = resolveProp(
    props.telemetryEnabled,
    ['debug.telemetry.enabled', 'telemetry.enabled', 'telemetryEnabled'],
    false
  );
  const telemetryEndpointRaw = resolveProp(
    props.telemetryEndpoint,
    ['debug.telemetry.endpoint', 'telemetry.endpoint', 'telemetryEndpoint'],
    null
  );
  const telemetryPollIntervalMs = resolveProp(
    props.telemetryPollIntervalMs,
    ['debug.telemetry.pollIntervalMs', 'telemetry.pollIntervalMs', 'telemetryPollIntervalMs'],
    5000
  );
  const telemetryShowContext = resolveProp(
    props.telemetryShowContext,
    ['debug.telemetry.showContext', 'telemetry.showContext', 'telemetryShowContext'],
    true
  );

  const telemetryEndpoint = useMemo(() => {
    if (telemetryEndpointRaw) return telemetryEndpointRaw;
    if (apiBaseUrl) return `${apiBaseUrl.replace(/\/$/, '')}/telemetry`;
    return '/api/response/telemetry';
  }, [telemetryEndpointRaw, apiBaseUrl]);

  return {
    widgetAuthToken,
    messageEndpoint,
    messageCreateEndpoint,
    streamEndpoint,
    threadEndpoint,
    threadCreateEndpoint,
    threadUpdateEndpoint,
    threadDeleteEndpoint,
    cartEndpoint,
    analyticsBaseUrl,
    analyticsEndpoint,
    analyticsSampleRate,
    analyticsEnabled,
    analyticsApiKey,
    telemetryEnabled,
    telemetryPollIntervalMs,
    telemetryShowContext,
    telemetryEndpoint,
  };
}
